import { useRef, useEffect, useCallback } from 'react';
import './FillTextQuestion.css';

const FillTextQuestion = ({
  question,
  textAnswer,
  onTextChange,
  onSubmitAnswer,
  isSubmitting = false,
}) => {
  const inputRef = useRef();

  const maxLength = 16;

  const handleChange = (e) => {
    const value = e.target.value;
    const cleaned = value.replace(/\s/g, '');
    
    if (cleaned.length <= maxLength) {
      onTextChange(cleaned);
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    onSubmitAnswer(e);
  };

  const handleKeyDown = (e) => {
    // Get key information with fallbacks for cross-browser compatibility
    const key = e.key || e.code;
    const keyCode = e.keyCode || e.which;
    
    // Define blocked navigation keys with both modern and legacy key codes
    const blockedKeys = {
      // Arrow keys
      'ArrowLeft': 37,
      'ArrowRight': 39,
      'ArrowUp': 38,
      'ArrowDown': 40,
      // Additional navigation keys that should be blocked
      'Home': 36,
      'End': 35,
      'PageUp': 33,
      'PageDown': 34
    };
    
    // Check if the pressed key should be blocked
    const isBlockedKey = Object.entries(blockedKeys).some(([keyName, code]) => {
      return key === keyName || keyCode === code;
    });
    
    if (isBlockedKey) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    // Allow specific functional keys
    const allowedFunctionalKeys = {
      'Backspace': 8,
      'Delete': 46,
      'Tab': 9,
      'Escape': 27,
      'Enter': 13
    };
    
    const isAllowedFunctional = Object.entries(allowedFunctionalKeys).some(([keyName, code]) => {
      return key === keyName || keyCode === code;
    });
    
    // Allow all printable characters except space
    const isSpace = key === ' ' || key === 'Space' || keyCode === 32;
    const isPrintableChar = (
      (keyCode >= 32 && keyCode <= 126) || // Standard printable ASCII
      (keyCode >= 160 && keyCode <= 255) || // Extended ASCII
      (key && key.length === 1 && key !== ' ') // Any single character except space
    );
    
    // Allow modifier keys (Ctrl, Alt, Shift, Meta) for system shortcuts
    const isModifierKey = e.ctrlKey || e.altKey || e.metaKey;
    
    // Block spaces specifically
    if (isSpace) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    // Block any key that's not explicitly allowed
    if (!isAllowedFunctional && !isPrintableChar && !isModifierKey) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    return true;
  };

  const handleInput = useCallback((e) => {
    // Check for unwanted characters that might have been inserted by autocorrect
    const currentValue = e.target.value;
    const hasSpaces = /\s/.test(currentValue);
    
    if (hasSpaces) {
      // If spaces were somehow inserted, clean them immediately
      const cleaned = currentValue.replace(/\s/g, '');
      if (cleaned !== textAnswer && cleaned.length <= maxLength) {
        onTextChange(cleaned);
      }
    }
    
    // Always ensure cursor is at the end after any input
    // Use requestAnimationFrame for better performance than setTimeout
    requestAnimationFrame(() => {
      try {
        if (inputRef.current && document.activeElement === inputRef.current) {
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      } catch (error) {
        // Fallback for browsers that don't support setSelectionRange
        console.warn('Could not set cursor position:', error);
      }
    });
  }, [textAnswer, onTextChange, maxLength]);

  const handleSlotClick = useCallback(() => {
    if (inputRef.current && !isSubmitting) {
      try {
        inputRef.current.focus();
        // Always position cursor at the end
        requestAnimationFrame(() => {
          if (inputRef.current && document.activeElement === inputRef.current) {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        });
      } catch (error) {
        console.warn('Could not focus input or set cursor position:', error);
      }
    }
  }, [isSubmitting]);

  // Handle touch events for better mobile support
  const handleTouchStart = useCallback((e) => {
    // Prevent default touch behavior that might interfere with cursor positioning
    if (inputRef.current && !isSubmitting) {
      handleSlotClick();
    }
  }, [handleSlotClick, isSubmitting]);

  // Enhanced mobile autofocus and scroll handling
  useEffect(() => {
    const focusInput = () => {
      if (inputRef.current && !isSubmitting) {
        setTimeout(() => {
          try {
            if (inputRef.current) {
              inputRef.current.focus();
              
              // Always position cursor at the end
              const length = inputRef.current.value.length;
              inputRef.current.setSelectionRange(length, length);
              
              // Mobile-specific scroll behavior
              if (window.innerWidth <= 768 && inputRef.current.scrollIntoView) {
                inputRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
              }
            }
          } catch (error) {
            console.warn('Could not focus input during initialization:', error);
          }
        }, 100);
      }
    };

    focusInput();

    const handleVisibilityChange = () => {
      if (!document.hidden && !isSubmitting) {
        focusInput();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitting]);

  return (
    <div className='text-input-wrapper'>
      <div className='fill-text-answer-section'>
        <form onSubmit={handleSubmit} className='text-input-form'>
          <div style={{ position: 'relative' }}>
            <label
              className='fill-text-form-label'
              style={{ paddingTop: '18px' }}
            >
              La tua risposta{' '}
              <span className='text-muted' style={{ fontSize: '0.75em' }}>
                (max 16 caratteri)
              </span>
            </label>
          </div>
          <div className='fill-text-input-wrapper'>
            <div
              className='fill-text-input-16-char-wrapper'
              onClick={handleSlotClick}
              onTouchStart={handleTouchStart}
            >
              <input
                ref={inputRef}
                value={textAnswer}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                placeholder={
                  question.placeholder || 'Inserisci la tua risposta...'
                }
                maxLength={maxLength}
                disabled={isSubmitting}
                className='fill-text-input-16-char'
                type='text'
                autoFocus
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck='false'
                inputMode='text'
                data-gramm='false'
              />
              <div className='fill-text-visual-slots'>
                {Array.from({ length: maxLength }, (_, index) => (
                  <div
                    key={index}
                    className={`fill-text-slot ${
                      index === textAnswer.length ? 'active' : ''
                    }`}
                  >
                    {textAnswer[index] || ''}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='submit-container'>
            <button
              type='button'
              onClick={handleButtonClick}
              disabled={isSubmitting}
              className={isSubmitting ? 'submitting' : ''}
            >
              {isSubmitting ? 'Invio in corso...' : 'Invia risposta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FillTextQuestion;
